<template>
  <section>
    <custom-design
      title="BACK"
      :base-tag-svg-img-path="baseTagSvgImgPath ? getImage(baseTagSvgImgPath) : false"
      :design-svg-img-path="false"
      :pet-name-text="redesignedTextConfig.petNameText"
      :phone-text="redesignedTextConfig.phoneText"
      :home-safe-id-text="redesignedTextConfig.homeSafeIdText"
      :left-zone-line1-text="redesignedTextConfig.leftZoneLine1Text"
      :left-zone-line2-text="redesignedTextConfig.leftZoneLine2Text"
      :hotline-text="redesignedTextConfig.hotlineText"
      :url-text="redesignedTextConfig.urlText"
      :show-redesigned-tag-back="true"
      :show-labels="showLabels"
      :scale="scale"
      :height="height"
      :boundary-padding="25"
      canvas-b-g-color="#f7f7f7"
    />
  </section>
</template>

<script>
import getImage from '../mixins/getImage';
import CustomDesign from './CustomDesign';

export default {
  name: 'PersonalizePreview',
  components: { CustomDesign },
  mixins: [getImage],
  props: {
    personalizationSelected: {
      type: Boolean,
      default: true,
      required: false,
    },
    baseTagSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: false,
    },
    showLabels: {
      type: Boolean,
      default: true,
      required: false,
    },
    height: {
      type: Number,
      default: 500,
      required: false,
    },
    scale: {
      type: Number,
      default: 0.8,
      required: false,
    },
    petName: {
      type: String,
      default: 'Found me?',
      required: true,
    },
    homeSafeId: {
      type: String,
      default: 'U237JC9Z',
      required: false,
    },
    phoneNumber: {
      type: [String, Number],
      default: '',
      required: true,
    },
    leftZoneLine1: {
      type: String,
      default: 'Scan here',
      required: false,
    },
    leftZoneLine2: {
      type: String,
      default: null,
      required: false,
    },
    hotlineNumber: {
      type: String,
      default: '',
      required: false,
    },
    tagBackUrl: {
      type: String,
      default: 'www.docupet.com',
      required: false,
    },
    defaultPhoneNumber: {
      type: [String, Number],
      default: '',
      required: false,
    },
  },
  computed: {
    leftZoneLine1Text() {
      return {
        offsetX: this.leftZoneLine2 ? 148 : 118,
        offsetY: 0,
        boundaryPadding: -74,
        color: 'black',
        text: this.leftZoneLine1,
        minFontSize: 18,
        maxFontSize: this.leftZoneLine2 ? 28 : 30,
        fontFamily: 'Lato',
        options: {
          fontSize: this.leftZoneLine2 ? 28 : 30,
          angle: -90,
          fontWeight: 400,
        },
      };
    },
    leftZoneLine2Text() {
      if (!this.leftZoneLine2) return false;
      return {
        offsetX: 118,
        offsetY: 0,
        boundaryPadding: -74,
        color: 'black',
        text: this.leftZoneLine2,
        minFontSize: 18,
        maxFontSize: 28,
        fontFamily: 'Lato',
        options: {
          fontSize: 28,
          angle: -90,
          fontWeight: 400,
        },
      };
    },
    hasCustomPhoneNumber() {
      if (!this.personalizationSelected || !this.phoneNumber) return false;
      const enteredDigits = this.phoneNumber.toString().replace(/\D/g, '');
      const defaultDigits = this.defaultPhoneNumber?.toString()?.replace(/\D/g, '') || '';
      return enteredDigits.length > 0 && enteredDigits !== defaultDigits;
    },
    showPhoneLine() {
      if (!this.personalizationSelected) return false;
      const enteredDigits = this.phoneNumber?.toString()?.replace(/\D/g, '') || '';
      const defaultDigits = this.defaultPhoneNumber?.toString()?.replace(/\D/g, '') || '';
      return !(defaultDigits.length > 0 && enteredDigits === defaultDigits);
    },
    redesignedTextConfig() {
      return {
        petNameText: {
          offsetX: 0,
          offsetY: this.showPhoneLine ? -154 : -134,
          boundaryPadding: 60,
          color: 'black',
          text: this.petName || (this.personalizationSelected ? '(Pet Name)' : 'Found me?'),
          minFontSize: 24,
          maxFontSize: 36,
          maxCharacters: 10,
          fontFamily: 'Lato',
          options: {
            fontSize: 24,
          },
        },
        phoneText: this.showPhoneLine
          ? {
              offsetX: 0,
              offsetY: -115,
              boundaryPadding: 60,
              color: 'black',
              text: this.hasCustomPhoneNumber ? this.phoneNumberFormatter(this.phoneNumber) : '(Phone Number)',
              minFontSize: 24,
              maxFontSize: 36,
              fontFamily: 'Lato',
              options: {
                fontSize: 24,
              },
            }
          : false,
        homeSafeIdText: {
          offsetX: -118,
          offsetY: 0,
          boundaryPadding: 20,
          color: 'black',
          text: this.homeSafeId?.toUpperCase(),
          minFontSize: 30,
          maxFontSize: 32,
          maxCharacters: 8,
          fontFamily: 'Lato',
          options: {
            fontSize: 32,
            angle: 90,
          },
        },
        leftZoneLine1Text: this.leftZoneLine1Text,
        leftZoneLine2Text: this.leftZoneLine2Text,
        hotlineText: {
          offsetX: 0,
          offsetY: 154,
          boundaryPadding: 30,
          color: 'black',
          text: this.hotlineNumber,
          minFontSize: 24,
          maxFontSize: 36,
          fontFamily: 'Lato',
          options: {
            fontSize: 24,
          },
        },
        urlText: {
          offsetX: 0,
          offsetY: 117,
          boundaryPadding: 30,
          color: 'black',
          text: this.tagBackUrl,
          minFontSize: 24,
          maxFontSize: 32,
          fontFamily: 'Lato',
          options: {
            fontSize: 24,
          },
        },
      };
    },
  },
  methods: {
    phoneNumberFormatter(phone) {
      const phoneStr = phone != null ? String(phone) : '';
      if (phoneStr.length > 0) {
        let x = phoneStr.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        return !x[2] ? x[1] : x[1] + '-' + x[2] + (x[3] ? '-' + x[3] : '');
      }
      return '';
    },
  },
};
</script>